import { motion } from "framer-motion";
import { PageHeader, PolicyCard, PolicyRow, ContactNudge } from "../components/PolicyHelpers";

export default function PrivacyPolicy() {
  return (
    <div className="bg-white" style={{ paddingTop: 64 }}>
      <div className="max-w-2xl mx-auto px-4 py-12">

        <PageHeader
          eyebrow="Town Square Arena"
          title="Privacy Policy"
          subtitle="How we collect, use, and protect your personal information."
        />

        <p className="text-black/35 text-xs -mt-6 mb-8">Last updated: April 2026</p>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: 0.08 }}>
          <PolicyCard>
            <PolicyRow icon="ℹ" iconBg="#eff6ff" iconBorder="#bfdbfe" iconColor="#3b82f6" title="About This Policy">
              This Privacy Policy describes how <span className="text-black font-medium">Town Square Arena</span> ("we", "us", or "our"), operating at{" "}
              <span className="text-black font-medium">townsquarearena.com</span>, collects, uses, and protects your personal information when you use our website and booking services.
            </PolicyRow>

            <PolicyRow icon="📝" iconBg="#f0fdf4" iconBorder="#bbf7d0" iconColor="#16a34a" title="Information We Collect">
              We may collect the following information when you make a booking or use our website:
              <ul className="mt-1.5 space-y-0.5 list-disc list-inside">
                <li>Full name and phone number</li>
                <li>Email address (if voluntarily provided)</li>
                <li>Booking details (date, time, sport, number of slots)</li>
                <li>Payment information (processed securely by Razorpay)</li>
                <li>IP address and basic device/browser information</li>
              </ul>
            </PolicyRow>

            <PolicyRow icon="⚙" iconBg="#fafafa" iconBorder="#e5e7eb" iconColor="#6b7280" title="How We Use Your Information">
              Your information is used solely for:
              <ul className="mt-1.5 space-y-0.5 list-disc list-inside">
                <li>Processing and confirming your bookings</li>
                <li>Sending booking confirmations and reminders via WhatsApp</li>
                <li>Responding to your enquiries and support requests</li>
                <li>Improving our website and services</li>
                <li>Complying with applicable legal obligations</li>
              </ul>
            </PolicyRow>

            <PolicyRow icon="💳" iconBg="#fffbeb" iconBorder="#fde68a" iconColor="#f59e0b" title="Payment Security">
              All payment transactions are processed by <span className="text-black font-medium">Razorpay</span>, which is{" "}
              <span className="text-black font-medium">PCI DSS compliant</span> and authorised by the Reserve Bank of India. We do{" "}
              <span className="font-medium text-black">not</span> store, log, or have access to your credit/debit card numbers, CVV details, UPI credentials, or any payment instrument data. This policy complies with{" "}
              <span className="text-black font-medium">RBI guidelines for payment aggregators</span>. Payment data is handled entirely by Razorpay in accordance with their privacy policy and applicable RBI regulations.
            </PolicyRow>

            <PolicyRow icon="🗄" iconBg="#f5f3ff" iconBorder="#ddd6fe" iconColor="#7c3aed" title="Data Storage & Retention">
              Your booking and account data is stored securely on <span className="text-black font-medium">Google Firebase</span> servers, which comply with international data security standards. Booking records are retained for a minimum of{" "}
              <span className="text-black font-medium">3 years</span> from the date of booking for legal and accounting compliance purposes. After this period, data may be deleted or anonymised unless retention is required by law.
            </PolicyRow>

            <PolicyRow icon="🤝" iconBg="#f0fdf4" iconBorder="#bbf7d0" iconColor="#16a34a" title="Data Sharing">
              We do <span className="text-black font-medium">not sell, rent, or trade</span> your personal data to third parties. Your data may be shared with:
              <ul className="mt-1.5 space-y-0.5 list-disc list-inside">
                <li><span className="font-medium text-black">Razorpay</span> — solely for payment processing</li>
                <li>Law enforcement or regulatory authorities if required by law</li>
              </ul>
            </PolicyRow>

            <PolicyRow icon="🍪" iconBg="#fffbeb" iconBorder="#fde68a" iconColor="#f59e0b" title="Cookies">
              We use basic cookies to ensure the website functions correctly (e.g., maintaining your session and preferences). We do not use cookies for advertising or cross-site tracking. You may disable cookies in your browser settings, though some features may not function as intended.
            </PolicyRow>

            <PolicyRow icon="✋" iconBg="#fef2f2" iconBorder="#fca5a5" iconColor="#ef4444" title="Your Rights">
              You have the right to:
              <ul className="mt-1.5 space-y-0.5 list-disc list-inside">
                <li>Request access to the personal data we hold about you</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your personal data</li>
              </ul>
              <span className="block mt-1.5">
                To exercise any of these rights, email us at{" "}
                <a href="mailto:support@townsquarearena.com" className="text-[#b45309] underline">support@townsquarearena.com</a>.
                We will respond within 30 days.
              </span>
            </PolicyRow>

            <PolicyRow icon="⚖" iconBg="#eff6ff" iconBorder="#bfdbfe" iconColor="#3b82f6" title="Governing Law">
              This Privacy Policy is governed by the{" "}
              <span className="text-black font-medium">Information Technology Act, 2000</span> and the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011, along with other applicable Indian laws. By using our services, you consent to the collection and use of your information as described in this policy.
            </PolicyRow>

            <PolicyRow icon="🧑‍💼" iconBg="#fef2f2" iconBorder="#fca5a5" iconColor="#ef4444" title="Grievance Officer">
              In accordance with the Information Technology Act, 2000 and rules thereunder, the name and contact details of the Grievance Officer are provided below. If you have any complaints or concerns regarding the processing of your personal data, please contact:
              <span className="block mt-2 p-3 rounded-xl border border-black/8 bg-white space-y-0.5">
                <span className="block font-medium text-black">Town Square Arena Support Team</span>
                <span className="block">
                  📧{" "}
                  <a href="mailto:support@townsquarearena.com" className="text-[#b45309] underline">support@townsquarearena.com</a>
                </span>
                <span className="block">
                  📞{" "}
                  <a href="tel:+919133303044" className="text-[#b45309] underline">+91 91333 03044</a>
                </span>
                <span className="block text-black/50">Grievances will be acknowledged and resolved within <span className="text-black font-medium">30 days</span> as required under the IT Act, 2000.</span>
              </span>
            </PolicyRow>

            <PolicyRow icon="📍" iconBg="#f0fdf4" iconBorder="#bbf7d0" iconColor="#15803d" title="Contact & Address" last>
              <span className="block font-medium text-black">Town Square Arena</span>
              Town Square Suites, 6-114/51/2/A,<br />
              Shamshabad - Mamidipally Rd, Shamshabad,<br />
              Hyderabad, Telangana 501218<br />
              <span className="block mt-1.5">
                📧{" "}
                <a href="mailto:support@townsquarearena.com" className="text-[#b45309] underline">support@townsquarearena.com</a>
                {"  "}·{"  "}
                📞{" "}
                <a href="tel:+919133303044" className="text-[#b45309] underline">+91 91333 03044</a>
              </span>
            </PolicyRow>
          </PolicyCard>
        </motion.div>

        <ContactNudge />
      </div>
    </div>
  );
}
